import { observer } from "mobx-react";
import { useEffect } from "react";
import { ShareTransferConsentForm } from "./shareTransferConsentDomain";
import {
  CheckboxGroupQuestion,
  NumberQuestion,
  TextQuestion,
  YesNoQuestion,
} from "@/dbot/common/commonComponents";
import { LocalizedTextEditor } from "@/text/LocalizedTextEditor";
import { Box, VBox } from "@/components-v2/Box";
import { Alert } from "@/components-v2/Alert";
import { Button } from "@/components-v2/Button";
import { MSelect } from "@/components-v2/Select";
import { SelectActorsDialog } from "@/dialogs/dialogComponents";
import { CURRENCY_NOK } from "@/utils/currency";
import { useTranslation } from "@/stores/RootStoreContext";

export const ShareTransferConsentView = observer(function ShareTransferConsentView({
  form,
}: {
  form: ShareTransferConsentForm;
}) {
  const { t } = useTranslation();
  const locked = form.isProjectLocked;

  useEffect(() => form.init(), [form]);

  if (form.shareholders.length === 0) {
    return <Alert type={"error"} message={t("decisions.shareTransferConsent.noShareholders")} />;
  }

  return (
    <VBox gap={"lg"}>
      <div>{t("decisionType.shareTransferConsent.text")}</div>
      <VBox>
        <div>{t("decisions.shareTransferConsent.transferor.title")}</div>
        <MSelect
          model={form.transferor}
          placeholder={t("decisions.shareTransferConsent.transferor.placeholder")}
          disabled={locked}
          fullWidth
        />
      </VBox>
      <VBox>
        <TextQuestion
          question={t("decisions.shareTransferConsent.acquirer.title")}
          field={form.acquirerName}
          onChange={form.typeAcquirerName}
          isLocked={locked}
        />
        {!locked && (
          <Box>
            <Button
              variant={"secondary"}
              size={"sm"}
              label={t("decisions.shareTransferConsent.acquirer.selectExisting")}
              onClick={form.openAcquirerDialog}
            />
          </Box>
        )}
      </VBox>
      <NumberQuestion
        question={t("decisions.shareTransferConsent.numberOfShares.title")}
        infoText={
          form.transferor.value
            ? t("decisions.shareTransferConsent.numberOfShares.held", {
                name: form.transferor.value.name,
                count: form.transferorNumberOfShares,
              })
            : undefined
        }
        field={form.numberOfShares}
        isLocked={locked}
        inline
      />
      {form.isAllSharesInCompany && (
        <Alert
          type={"info"}
          message={t("decisions.shareTransferConsent.numberOfShares.allShares")}
        />
      )}
      <NumberQuestion
        question={t("decisions.shareTransferConsent.pricePerShare.title")}
        field={form.pricePerShare}
        prefix={CURRENCY_NOK}
        isLocked={locked}
        inline
      />
      <YesNoQuestion
        question={t("decisions.shareTransferConsent.isConsentGiven.title")}
        field={form.isConsentGiven}
        fieldName={"isConsentGiven"}
        isLocked={locked}
      />
      {form.isConsentGiven.value === true && (
        <CheckboxGroupQuestion
          question={{ key: "decisions.shareTransferConsent.conditions.title" }}
          field={form.conditions}
          isLocked={locked}
        />
      )}
      {form.isOtherConditionSelected && (
        <LocalizedTextEditor model={form.otherCondition} inputType={"string"} />
      )}
      {form.isRefused && <LocalizedTextEditor model={form.refusalGrounds} inputType={"string"} />}
      {form.acquirerDialog && <SelectActorsDialog model={form.acquirerDialog} />}
    </VBox>
  );
});
